import {
  createContext, useContext, useState, useEffect, useRef, useCallback, useMemo,
  type Dispatch, type ReactNode, type SetStateAction,
} from 'react';
import { createNotification, type AppNotification } from '../core/models/index';
import {
  isNotificationExpired,
  keepActiveNotifications,
  dropReadForIncoming,
  claimNotificationId,
} from '../core/notifications/expiration';
import {
  filterOutMatchingIntegrity,
  matchesIntegrityNotification,
  type IntegrityInboxMatch,
} from '../core/notifications/integrityInbox';
import { DEFAULT_PREFS, seedNotifs } from '../services/NotificationService';
import { playNotificationSound } from '../services/notificationSound';
import {
  loadNotificationPreferences,
  saveNotificationPreferences,
} from '../services/userPreferences';
import { cubaMinutesOfDay } from '../core/time/cubaTime';
import { getServerNow } from '../services/time/serverClock';
import { DATA_ADAPTER } from '../config/adapters';
import type { Language, Role } from '../types';

const TOAST_DURATION_MS = 5000;
const TOAST_GAP_MS = 300;
const FLUSH_DELAY_MS = 500;
const EXPIRATION_CHECK_MS = 15_000;

export type NotifPrefs = Record<string, boolean>;
export type QuietHours = { enabled: boolean; from: string; to: string };

export type NotifNavigateMeta = {
  playId?: string;
  focus?: string;
  phase?: string;
  ticketId?: string;
  [key: string]: unknown;
};

export type NotifContextValue = {
  items: AppNotification[];
  unreadCount: number;
  push: (fields: Partial<AppNotification>, source?: string) => AppNotification | null;
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  /** Remove integrity inbox rows that match a suppressed / FP signal. */
  dismissMatchingIntegrity: (match: IntegrityInboxMatch) => void;
  clearAll: () => void;
  activeToast: AppNotification | null;
  dismissToast: () => void;
  handleAction: (
    notifId: string,
    actionId: string,
    target: string,
    actionMeta?: NotifNavigateMeta,
  ) => void;
  prefs: NotifPrefs;
  setPrefs: Dispatch<SetStateAction<NotifPrefs>>;
  quietHours: QuietHours;
  setQuietHours: Dispatch<SetStateAction<QuietHours>>;
  sound: boolean;
  setSound: Dispatch<SetStateAction<boolean>>;
  pushEnabled: boolean;
  setPushEnabled: Dispatch<SetStateAction<boolean>>;
  /** False until stored notification prefs have been applied (avoid settings flash). */
  prefsReady: boolean;
  isInQuietHours: () => boolean;
  pendingQueue: AppNotification[];
  scheduledLog: Array<AppNotification & { dispatchedAt: string }>;
  role: Role | string;
};

const NotifCtx = createContext<NotifContextValue | null>(null);

export function NotifProvider({
  children,
  role,
  lang,
  online,
  navigate,
  userId = null,
}: {
  children: ReactNode;
  role: Role | string;
  lang: Language | string;
  online: boolean;
  navigate?: (target: string, meta?: NotifNavigateMeta) => void;
  userId?: string | null;
}) {
  const roleDefaults = useMemo(
    () => (DEFAULT_PREFS as Record<string, NotifPrefs>)[role] ?? {},
    [role],
  );
  const [items, setItems] = useState<AppNotification[]>([]);
  const [prefsBoot] = useState(() => loadNotificationPreferences(
    (DEFAULT_PREFS as Record<string, NotifPrefs>)[role] ?? {},
    userId,
  ));
  const [prefs, setPrefs] = useState<NotifPrefs>(prefsBoot.prefs);
  const [quietHours, setQuietHours] = useState<QuietHours>(prefsBoot.quietHours);
  const [sound, setSound] = useState(prefsBoot.sound);
  const [pushEnabled, setPushEnabled] = useState(prefsBoot.pushEnabled);
  const [prefsReady, setPrefsReady] = useState(true);
  const [activeToast, setActiveToast] = useState<AppNotification | null>(null);
  const [pendingQueue, setPendingQueue] = useState<AppNotification[]>([]);
  const [scheduledLog, setScheduledLog] = useState<Array<AppNotification & { dispatchedAt: string }>>([]);

  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastSeenIdsRef = useRef(new Set<string>());
  const flushRunningRef = useRef(false);
  const pendingRef = useRef<AppNotification[]>([]);
  const prefsUserKeyRef = useRef(`${role}:${userId ?? ''}`);

  useEffect(() => {
    const key = `${role}:${userId ?? ''}`;
    if (key === prefsUserKeyRef.current) return;
    prefsUserKeyRef.current = key;
    setPrefsReady(false);
    const stored = loadNotificationPreferences(roleDefaults, userId);
    setPrefs(stored.prefs);
    setQuietHours(stored.quietHours);
    setSound(stored.sound);
    setPushEnabled(stored.pushEnabled);
    setPrefsReady(true);
  }, [role, userId, roleDefaults]);

  useEffect(() => {
    if (DATA_ADAPTER === 'mock') {
      setItems(seedNotifs(role, lang) as AppNotification[]);
    } else {
      setItems([]);
    }
  }, [role, lang]);

  useEffect(() => {
    if (!prefsReady || !userId) return;
    saveNotificationPreferences(userId, { prefs, quietHours, sound, pushEnabled });
  }, [prefsReady, userId, prefs, quietHours, sound, pushEnabled]);

  useEffect(() => {
    const removeExpired = () => {
      const now = getServerNow().getTime();
      setItems((current) => keepActiveNotifications(current, now));
      setPendingQueue((current) => {
        const active = keepActiveNotifications(current, now);
        pendingRef.current = active;
        return active;
      });
      setActiveToast((current) => (
        current && isNotificationExpired(current, now) ? null : current
      ));
    };

    removeExpired();
    const id = setInterval(removeExpired, EXPIRATION_CHECK_MS);
    return () => clearInterval(id);
  }, []);

  const isInQuietHours = () => {
    if (!quietHours.enabled) return false;
    const cur = cubaMinutesOfDay(getServerNow());
    const [fh, fm] = quietHours.from.split(':').map(Number);
    const [th, tm] = quietHours.to.split(':').map(Number);
    const f = fh * 60 + fm;
    const t = th * 60 + tm;
    return f > t ? (cur >= f || cur < t) : (cur >= f && cur < t);
  };

  const shouldShowToast = useCallback((notif: AppNotification) => {
    // Interruptive overlay is exceptional — inbox covers routine operational noise.
    if (!notif.urgent) return false;
    if (!pushEnabled || !prefs[notif.cat]) return false;
    return true;
  }, [pushEnabled, prefs]);

  const showToast = useCallback((notif: AppNotification) => {
    setActiveToast(notif);
    if (sound) playNotificationSound();
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setItems((prev) => prev.map((n) => (n.id === notif.id ? { ...n, unread: false } : n)));
      setActiveToast((t) => (t?.id === notif.id ? null : t));
    }, TOAST_DURATION_MS);
  }, [sound]);

  const push = (fields: Partial<AppNotification>, source = 'manual') => {
    const notif = createNotification(fields);
    if (isNotificationExpired(notif, getServerNow().getTime())) return null;

    if (source === 'scheduler') {
      setScheduledLog((l) => [{ ...notif, dispatchedAt: new Date().toISOString() }, ...l].slice(0, 30));
    }

    setItems((prev) => dropReadForIncoming(prev, notif));
    const firstForId = claimNotificationId(toastSeenIdsRef.current, notif.id);
    if (firstForId && shouldShowToast(notif)) {
      if (!online) {
        setPendingQueue((q) => {
          const next = [...q, notif];
          pendingRef.current = next;
          return next;
        });
        return notif;
      }
      showToast(notif);
    }

    return notif;
  };

  useEffect(() => {
    if (!online || pendingRef.current.length === 0 || flushRunningRef.current) return undefined;

    flushRunningRef.current = true;
    const queue = keepActiveNotifications(pendingRef.current, getServerNow().getTime());
    pendingRef.current = [];
    setPendingQueue([]);

    let cancelled = false;
    let index = 0;

    const processNext = () => {
      if (cancelled || index >= queue.length) {
        flushRunningRef.current = false;
        return;
      }
      const notif = queue[index++];
      if (isNotificationExpired(notif, getServerNow().getTime())) {
        processNext();
        return;
      }
      setActiveToast(notif);
      if (sound) playNotificationSound();
      setTimeout(() => {
        if (cancelled) return;
        setActiveToast(null);
        setTimeout(processNext, TOAST_GAP_MS);
      }, TOAST_DURATION_MS);
    };

    const startId = setTimeout(processNext, FLUSH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(startId);
      flushRunningRef.current = false;
    };
  }, [online, sound]);

  const markRead = (id: string) => setItems((p) => p.map((n) => (n.id === id ? { ...n, unread: false } : n)));
  const markAllRead = () => setItems((p) => p.map((n) => ({ ...n, unread: false })));
  const dismiss = (id: string) => setItems((p) => p.filter((n) => n.id !== id));
  const dismissMatchingIntegrity = useCallback((match: IntegrityInboxMatch) => {
    setItems((prev) => {
      const next = filterOutMatchingIntegrity(prev, match);
      return next.length === prev.length ? prev : next;
    });
    setActiveToast((toast) => (
      toast && matchesIntegrityNotification(toast, match) ? null : toast
    ));
  }, []);
  const clearAll = () => setItems([]);
  const dismissToast = () => setActiveToast(null);

  const handleAction = (
    notifId: string,
    _actionId: string,
    target: string,
    actionMeta?: NotifNavigateMeta,
  ) => {
    const notif = items.find((n) => n.id === notifId);
    markRead(notifId);
    dismissToast();
    navigate?.(target, { ...(notif?.meta ?? {}), ...(actionMeta ?? {}) });
  };

  const unreadCount = items.filter((n) => n.unread).length;

  return (
    <NotifCtx.Provider value={{
      items, unreadCount, push, markRead, markAllRead, dismiss, dismissMatchingIntegrity, clearAll,
      activeToast, dismissToast, handleAction,
      prefs, setPrefs, quietHours, setQuietHours,
      sound, setSound, pushEnabled, setPushEnabled, prefsReady,
      isInQuietHours, pendingQueue, scheduledLog, role,
    }}>
      {children}
    </NotifCtx.Provider>
  );
}

export const useNotifs = (): NotifContextValue => {
  const ctx = useContext(NotifCtx);
  if (!ctx) throw new Error('useNotifs must be used within NotifProvider');
  return ctx;
};
